import { useCallback, useRef, useState } from "react";

interface DropZoneProps {
	onFiles: (files: File[]) => void;
	disabled?: boolean;
	maxFiles?: number;
}

const ACCEPT_TYPES = ["image/jpeg", "image/png", "image/webp"];

export function DropZone({
	onFiles,
	disabled = false,
	maxFiles = 10,
}: DropZoneProps) {
	const [isDragging, setIsDragging] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	const processFiles = useCallback(
		(fileList: FileList | File[]) => {
			const files = Array.from(fileList)
				.filter((f) => ACCEPT_TYPES.includes(f.type))
				.slice(0, maxFiles);
			if (files.length > 0) onFiles(files);
		},
		[onFiles, maxFiles],
	);

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		if (!disabled) setIsDragging(true);
	};

	const handleDragLeave = () => setIsDragging(false);

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		setIsDragging(false);
		if (!disabled) processFiles(e.dataTransfer.files);
	};

	const handleClick = () => {
		if (!disabled) inputRef.current?.click();
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files) processFiles(e.target.files);
		e.target.value = "";
	};

	return (
		<div
			onDragOver={handleDragOver}
			onDragLeave={handleDragLeave}
			onDrop={handleDrop}
			onClick={handleClick}
			className={`
        border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all
        ${isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-blue-400 hover:bg-gray-50"}
        ${disabled ? "opacity-50 cursor-not-allowed" : ""}
      `}
		>
			<input
				ref={inputRef}
				type="file"
				accept={ACCEPT_TYPES.join(",")}
				multiple
				className="hidden"
				onChange={handleChange}
				disabled={disabled}
			/>
			<div className="flex flex-col items-center gap-3">
				<svg
					className="w-12 h-12 text-gray-400"
					fill="none"
					stroke="currentColor"
					viewBox="0 0 24 24"
				>
					<path
						strokeLinecap="round"
						strokeLinejoin="round"
						strokeWidth={1.5}
						d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
					/>
				</svg>
				<div>
					<p className="text-lg font-medium text-gray-700">
						画像をドロップ、またはクリックして選択
					</p>
					<p className="text-sm text-gray-500 mt-1">
						JPEG・PNG・WebP 対応（最大{maxFiles}枚）
					</p>
				</div>
			</div>
		</div>
	);
}
